// Embudo del Engine (revision UX del 26/10/2026): quien llega por una invitacion de un
// despacho no debe ver el alta de particulares (prueba de Pro, IRPF, onboarding). Todo
// se activa solo con una senal del Engine (eng=1 en el enlace, membresia); sin ella, el
// flujo de particulares queda igual. Funciones puras: las paginas solo las llaman.
import i18n from "@/i18n";
import { ENGINE_LANGS, resolveLanguage } from "@/i18n/languages";

// Solo rutas internas: "//host" o "/\host" las abre el navegador como otro dominio.
export function safeFrom(from) {
    if (typeof from !== "string" || !/^\/(?![/\\])/.test(from)) return null;
    return /[\\\s]/.test(from) ? null : from;
}

// El correo de invitacion enlaza /register?email=...&from=/app/engine&eng=1.
export function readEngineInvite(searchParams) {
    return {
        engine: searchParams.get("eng") === "1",
        email: (searchParams.get("email") || "").trim(),
        from: safeFrom(searchParams.get("from")),
    };
}

// Idioma de la variante Engine del registro y el acceso: el `lang` del enlace (el del despacho que
// invita, solo es o en; cualquier otro valor se ignora) y, sin el, el del navegador. Nunca rige en el B2C.
export function readEngineLang(searchParams, navigatorLanguages) {
    const lang = searchParams.get("lang");
    if (ENGINE_LANGS.includes(lang)) return lang;
    return resolveLanguage({ member: true, navigatorLanguages });
}

const norm = (e) => (e || "").trim().toLowerCase();

// La invitacion se acepta sola si el alta usa el mismo correo; con otro no hay despacho.
export const emailMismatch = (invited, typed) => Boolean(norm(invited) && norm(typed) && norm(invited) !== norm(typed));

// El backend marca onboarding_completed al dar de alta a un miembro del Engine: ese va a su
// destino (o al dashboard, que engineLanding cambia por el Engine); el resto, al onboarding.
export const registerLanding = (user, from) => (user?.onboarding_completed ? from || "/app/dashboard" : "/onboarding");

export const isEngineRoute = (pathname) => /^\/app\/engine(\/|$)/.test(pathname || "");

// Para Private: "check" = preguntar a /api/engine/me antes de mandar al onboarding. Un
// miembro con el onboarding a medias (cuentas anteriores a este arreglo) entra en el Engine.
export function onboardingGate(user, pathname, native) {
    if (!user || user.onboarding_completed) return "ok";
    return isEngineRoute(pathname) && !native ? "check" : "onboarding";
}

// Cabecera y menu del Engine en vez de los de particulares (sin prueba de Pro, selector de
// cartera personal, buscador de activos ni facturacion B2C). Dentro del Engine, para todo
// miembro (y mientras se comprueba si lo es, para no ensenar el banner de prueba y quitarlo
// despues). Fuera, solo si no tiene carteras personales ni esta en su parte personal.
// `personalCount` es null mientras no se sabe cuantas carteras personales tiene.
export function showEngineChrome({ member, loading, native, pathname, personalRoute, personalCount }) {
    if (native) return false;
    if (isEngineRoute(pathname)) return Boolean(member || loading);
    return Boolean(member) && !personalRoute && personalCount === 0;
}

const langQuery = (lang) => (lang ? `&lang=${lang}` : "");

export const loginHref = (from, lang) => (from ? `/login?from=${encodeURIComponent(from)}${langQuery(lang)}` : "/login");

// "Sign in" de la pagina de asesores: sin invitacion, quien no tiene cuenta pide el piloto (ver Login.jsx).
export const advisorsLoginHref = (lang) => `${loginHref("/app/engine", lang)}&via=advisors`;

// Desde el login de la invitacion ("Inicia sesion" lleva from=/app/engine) al registro del Engine.
export const registerHref = (from, lang) =>
    isEngineRoute(from) ? `/register?from=${encodeURIComponent(from)}&eng=1${langQuery(lang)}` : "/register";

const LEAD_FIELDS = new Set(["privacy_ok", "email", "name", "firm", "country"]);
const ADVISORS_ES = i18n.getFixedT("es", "advisors");

// Formulario de piloto de /asesores: el motivo concreto, no un "no se ha podido enviar".
// `t` es el del namespace advisors en el idioma de la pagina; el backend ya manda su message en ese idioma.
export function leadErrorMessage(err, t = ADVISORS_ES) {
    if (!err?.response) return t("leadErrors.network");
    const d = err.response.data?.detail;
    if (d && typeof d === "object" && !Array.isArray(d) && d.message) return d.message;
    if (Array.isArray(d) && d.length) {
        const field = (d[0].loc || []).slice(-1)[0];
        return LEAD_FIELDS.has(field) ? t(`leadErrors.fields.${field}`) : t("leadErrors.tooLong");
    }
    return t("leadErrors.generic");
}

// Resultado de la comprobación de /api/engine/me cuando falla: "no_member" si el servidor
// responde que no es miembro (403/404); "error" si no se ha podido comprobar (red, tiempo, 5xx).
export function engineCheckFailure(err) {
    const status = err?.response?.status;
    return status === 403 || status === 404 ? "no_member" : "error";
}
